import React, { useState, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Truck, Package, Clock, CheckCircle, XCircle, GripVertical, Loader2, AlertTriangle } from 'lucide-react';
import { dispatchAPI } from '../../services/api';
import { toast } from 'sonner';

const COLUMNS = [
  { id: 'Pending', label: 'Pending', icon: Clock, color: '#f59e0b', bg: 'bg-amber-50', border: 'border-amber-200', headerBg: 'bg-amber-500' },
  { id: 'In Transit', label: 'In Transit', icon: Package, color: '#3b82f6', bg: 'bg-blue-50', border: 'border-blue-200', headerBg: 'bg-blue-500' },
  { id: 'Delivered', label: 'Delivered', icon: CheckCircle, color: '#059669', bg: 'bg-emerald-50', border: 'border-emerald-200', headerBg: 'bg-emerald-500' },
  { id: 'Cancelled', label: 'Cancelled', icon: XCircle, color: '#dc2626', bg: 'bg-red-50', border: 'border-red-200', headerBg: 'bg-red-500' },
];

// Valid status transitions — forward only + cancel
const VALID_TRANSITIONS = {
  'Pending': ['In Transit', 'Cancelled'],
  'In Transit': ['Delivered', 'Cancelled'],
  'Delivered': [],      // Terminal
  'Cancelled': [],      // Terminal
};

const DispatchKanban = ({ dispatches, onRefresh, onViewDetail }) => {
  const [updatingId, setUpdatingId] = useState(null);

  // Group dispatches by status
  const grouped = COLUMNS.reduce((acc, col) => {
    acc[col.id] = dispatches.filter(d => d.status === col.id);
    return acc;
  }, {});

  const handleDragEnd = useCallback(async (result) => {
    const { draggableId, source, destination } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId) return;

    const fromStatus = source.droppableId;
    const toStatus = destination.droppableId;
    const allowed = VALID_TRANSITIONS[fromStatus] || [];

    if (!allowed.includes(toStatus)) {
      toast.error(`Cannot move from "${fromStatus}" to "${toStatus}"`, {
        description: allowed.length > 0
          ? `Allowed: ${allowed.join(', ')}`
          : `"${fromStatus}" is a terminal status`,
      });
      return;
    }

    setUpdatingId(draggableId);
    try {
      await dispatchAPI.update(draggableId, { status: toStatus });
      toast.success(`Dispatch moved to ${toStatus}`);
      onRefresh();
    } catch (err) {
      toast.error('Status update failed', { description: err.response?.data?.detail || 'Try again' });
    } finally {
      setUpdatingId(null);
    }
  }, [onRefresh]);

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="dispatch-kanban-board">
        {COLUMNS.map(col => (
          <KanbanColumn
            key={col.id}
            column={col}
            items={grouped[col.id] || []}
            updatingId={updatingId}
            onViewDetail={onViewDetail}
            isTerminal={VALID_TRANSITIONS[col.id].length === 0}
          />
        ))}
      </div>
    </DragDropContext>
  );
};

const KanbanColumn = ({ column, items, updatingId, onViewDetail, isTerminal }) => {
  const Icon = column.icon;

  return (
    <div className="flex flex-col min-h-[400px]" data-testid={`kanban-column-${column.id}`}>
      {/* Column header */}
      <div className={`flex items-center justify-between px-3 py-2.5 rounded-t-xl ${column.headerBg}`}>
        <div className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5 text-white" />
          <span className="text-xs font-bold text-white">{column.label}</span>
        </div>
        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white/25 text-[10px] font-bold text-white">
          {items.length}
        </span>
      </div>

      {/* Droppable area */}
      <Droppable droppableId={column.id} isDropDisabled={isTerminal && items.length > 0 ? false : false}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`flex-1 p-2 space-y-2 rounded-b-xl border-2 border-t-0 transition-colors duration-200 ${
              snapshot.isDraggingOver
                ? `${column.bg} ${column.border} border-dashed`
                : 'bg-gray-50/50 border-gray-100'
            }`}
            style={{ minHeight: 120 }}
          >
            {items.length === 0 && !snapshot.isDraggingOver && (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Icon className="w-6 h-6 text-gray-200 mb-1.5" />
                <p className="text-[10px] text-gray-400">No dispatches</p>
              </div>
            )}
            {items.map((dispatch, index) => (
              <Draggable
                key={dispatch.id}
                draggableId={dispatch.id}
                index={index}
                isDragDisabled={updatingId === dispatch.id || isTerminal}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    className={`group bg-white rounded-lg border transition-all duration-150 ${
                      snapshot.isDragging
                        ? 'shadow-xl border-blue-300 ring-2 ring-blue-100 rotate-[1deg] scale-[1.02]'
                        : 'shadow-sm border-gray-100 hover:shadow-md hover:border-gray-200'
                    } ${updatingId === dispatch.id ? 'opacity-60' : ''} ${isTerminal ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'}`}
                  >
                    {/* Drag handle + card content */}
                    <div className="flex items-start gap-2 p-3">
                      {!isTerminal && (
                        <div {...provided.dragHandleProps} className="mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <GripVertical className="w-3.5 h-3.5 text-gray-300" />
                        </div>
                      )}
                      {isTerminal && <div {...provided.dragHandleProps} />}
                      <div className="flex-1 min-w-0" onClick={() => onViewDetail(dispatch.id)} role="button" tabIndex={0}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[11px] font-mono font-bold text-gray-900 truncate">{dispatch.dispatch_number}</span>
                          {updatingId === dispatch.id && <Loader2 className="w-3 h-3 animate-spin text-blue-500 flex-shrink-0" />}
                        </div>
                        <p className="text-[11px] text-gray-700 font-medium truncate">{dispatch.customer_name || '—'}</p>
                        {dispatch.oc_number && (
                          <p className="text-[9px] text-gray-400 mt-0.5 truncate">OC: {dispatch.oc_number}</p>
                        )}
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-[9px] text-gray-400">{dispatch.dispatch_date || '—'}</span>
                          <div className="flex items-center gap-1">
                            {dispatch.vehicle_no && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-gray-100 rounded text-[8px] text-gray-500 font-medium">
                                <Truck className="w-2.5 h-2.5" /> {dispatch.vehicle_no}
                              </span>
                            )}
                            <span className="inline-flex px-1.5 py-0.5 bg-gray-100 rounded text-[8px] text-gray-500 font-medium">
                              {dispatch.items?.length || 0} items
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>

      {/* Terminal status indicator */}
      {isTerminal && items.length > 0 && (
        <div className="flex items-center gap-1 px-3 py-1.5 text-[9px] text-gray-400">
          <AlertTriangle className="w-2.5 h-2.5" />
          <span>Terminal status — cards cannot be moved</span>
        </div>
      )}
    </div>
  );
};

export default DispatchKanban;
